import React from "react";
import { Tooltip } from "flowbite-react";
import { AnomalyFinding, statusPillColorMap } from "@shared/interfaces/Anomaly";
import Loader from "@shared/components/Loader";

interface ErrorStatusProps {
  entry: AnomalyFinding;
  handleResolve: (id: string) => void;
  handleIgnore: (id: string) => void;
  setAnomalyData: React.Dispatch<any>;
  setIsXlsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  loadingIndex: string | null;
  setLoadingIndex: React.Dispatch<React.SetStateAction<string | null>>;
}

const ErrorStatus: React.FC<ErrorStatusProps> = ({
  entry,
  handleResolve,
  handleIgnore,
  setIsXlsModalOpen,
  setAnomalyData,
  setLoadingIndex,
  loadingIndex = null,
}) => {
  const showAnomaly = () => {
    setLoadingIndex(entry.id);

    const affectedMap = new Map<
      string,
      {
        sheet_name: string;
        column_name: string;
        ranges: [number, number][];
        total_affected: number;
      }
    >();

    // Step 1: Parse affected_cells
    (entry.affected_cells || []).forEach((cell) => {
      const key = `${cell.sheet_name || "Sheet1"}__${cell.column_name}`;
      affectedMap.set(key, {
        sheet_name: cell.sheet_name || "Sheet1",
        column_name: cell.column_name,
        ranges: cell.ranges,
        total_affected: cell.total_affected,
      });
    });

    // Step 2: Add missing columns from columns_affected
    if (entry.columns_affected) {
      Object.entries(entry.columns_affected).forEach(([sheetName, columns]) => {
        columns.forEach((colName: string) => {
          const key = `${sheetName}__${colName}`;
          if (!affectedMap.has(key)) {
            affectedMap.set(key, {
              sheet_name: sheetName,
              column_name: colName,
              ranges: [[0, -1]], // special range to highlight entire column
              total_affected: -1, // -1 means unknown
            });
          }
        });
      });
    }

    const parsedAffectedCells = Array.from(affectedMap.values());

    setAnomalyData(parsedAffectedCells);
    setIsXlsModalOpen(true);
  };

  return (
    <div className="border border-gray-200 rounded-xl p-4 my-4 bg-white shadow-sm relative w-full">
      <div className="flex justify-between items-start">
        <div className="text-xs 2xl:text-sm font-semibold text-gray-900 mb-2">
          {entry.title}
        </div>
        <div className="flex gap-2">
          {/* Resolve button hidden */}
          {/* {entry.resolution_status !== "Resolved" && (
            <Tooltip content="Resolve" placement="top" arrow={false}>
              <button type="button" onClick={() => handleResolve(entry.id)}>
                <img src="/status/edit.svg" alt="Edit" className="w-5 h-5" />
              </button>
            </Tooltip>
          )} */}

          {loadingIndex === entry.id && <Loader color="#888" />}
          <Tooltip content="View" placement="top" arrow={false}>
            <button
              type="button"
              className="disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loadingIndex === entry.id}
              onClick={showAnomaly}
            >
              <img
                src="/eye.svg"
                alt="View"
                className="w-5 h-5 bg-[#F2F4F7] rounded-full"
              />
            </button>
          </Tooltip>

          {entry.resolution_status === "Resolved" ? (
            <Tooltip content="Resolved" placement="top" arrow={false}>
              <button type="button" className="mr-[-4px]">
                <img
                  src="/status/checkmark.svg"
                  alt="Resolved"
                  className="w-[24px] h-[24px]"
                />
              </button>
            </Tooltip>
          ) : entry.resolution_status === "Ignored" ? (
            <Tooltip content="Ignored" placement="top" arrow={false}>
              <button type="button">
                <img
                  src="/status/close_small_green.svg"
                  alt="Resolved"
                  className="w-5 h-5"
                />
              </button>
            </Tooltip>
          ) : (
            <Tooltip content="Ignore" placement="top" arrow={false}>
              <button type="button" onClick={() => handleIgnore(entry.id)}>
                <img
                  src="/status/close_small.svg"
                  alt="Remove"
                  className="w-5 h-5"
                />
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="text-xs 2xl:text-sm mt-1">
        <div className="flex items-center gap-2 mt-2">
          <span className="text-gray-500 font-medium">Severity:</span>
          <span
            className={`px-2 py-0.5 text-[0.65rem] 2xl:text-xs rounded-full font-semibold ${
              statusPillColorMap[entry.severity]
            }`}
          >
            {entry.severity}
          </span>
        </div>
        <div className="mt-1">
          <span className="text-gray-500 font-medium">Affected columns:</span>{" "}
          <span className="text-gray-800">
            {entry.columns_affected &&
            typeof entry.columns_affected === "object"
              ? Array.from(
                  new Set(Object.values(entry.columns_affected).flat())
                ).join(", ")
              : "—"}
          </span>
        </div>
        <div className="mt-1">
          <span className="text-gray-500 font-medium">Required Action:</span>{" "}
          <span className="text-gray-800">{entry.required_action}</span>
        </div>
      </div>
    </div>
  );
};

export default ErrorStatus;
